import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "capabilities", label: "Capabilities" },
  { id: "quality", label: "Quality" },
  { id: "leadership", label: "Leadership" },
  { id: "contact", label: "Contact" },
];

/* Sections whose background is dark (navy / hero video / values).
 * When the navbar overlaps any of these, switch to dark tone (white nav). */
const DARK_SECTIONS = new Set(["home", "values"]);

const LOGO_DARK = "/logo-si.png";   // full lockup: SI mark + "SAMARTH" + "INFINITY"
const LOGO_LIGHT = "/logo-si.png";

const goTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const scrollToId = (id) => {
  if (id === "home") return goTop();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [overDark, setOverDark] = useState(true); // hero is dark by default
  const [activeId, setActiveId] = useState("home");
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      // sample a few pixels below the navbar to detect the section behind it
      const sampleY = 60; // px below top of viewport (inside navbar area)
      let current = "home";
      for (const n of NAV) {
        const el = document.getElementById(n.id);
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top <= sampleY + 40) current = n.id;
        }
      }
      // also check the values section (not in nav)
      const valEl = document.getElementById("values");
      if (valEl) {
        const top = valEl.getBoundingClientRect().top;
        const bottom = top + valEl.offsetHeight;
        if (top <= sampleY && bottom > sampleY) current = "values";
      }
      setActiveId(current);
      setOverDark(DARK_SECTIONS.has(current));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname, hash]);

  const handleNav = (e, id) => {
    e.preventDefault();
    if (pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => scrollToId(id), 80);
    } else {
      scrollToId(id);
    }
  };

  const dark = true; // Navbar locked to navy/blue at all times per client direction

  return (
    <header
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        dark
          ? "bg-[#00082A] border-b border-[rgba(200,150,31,0.25)]"
          : "bg-white border-b border-[#ECEEF3] shadow-[0_4px_22px_rgba(10,31,68,0.06)]",
      ].join(" ")}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1480px] flex items-center justify-between px-6 lg:px-12 h-[108px] md:h-[120px]">
        <a
          href="/#home"
          onClick={(e) => handleNav(e, "home")}
          data-testid="site-logo"
          className="flex items-center gap-3 group"
          aria-label="Samarth Infinity — home"
        >
          {/* Use transparent (gold-only) logo on dark; navy-bg logo on light */}
          <img
            src={dark ? LOGO_DARK : LOGO_LIGHT}
            alt="Samarth Infinity"
            className="h-20 md:h-24 w-auto block transition-transform duration-500 group-hover:scale-[1.03]"
            draggable="false"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`/#${n.id}`}
              onClick={(e) => handleNav(e, n.id)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={[
                "relative px-4 py-2 font-grotesque text-[12px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300",
                dark
                  ? (activeId === n.id ? "text-white" : "text-white/70 hover:text-white")
                  : (activeId === n.id ? "text-[#0A1F44]" : "text-[#5A6478] hover:text-[#0A1F44]"),
              ].join(" ")}
            >
              <span className="relative">
                {n.label}
                {activeId === n.id && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute left-0 right-0 -bottom-2 h-px bg-[#C8961F]"
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
              </span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contact"
            onClick={(e) => handleNav(e, "contact")}
            data-testid="nav-cta-enquire"
            className={dark ? "hidden md:inline-flex btn btn--outline-gold" : "hidden md:inline-flex btn btn--dark"}
            style={{ padding: "0.85rem 1.4rem" }}
          >
            Enquire <ArrowRight className="arrow h-4 w-4" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={[
              "lg:hidden h-11 w-11 inline-flex items-center justify-center border transition-colors",
              dark ? "border-white/40 text-white hover:bg-white hover:text-[#0A1F44]" : "border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white",
            ].join(" ")}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className={dark ? "lg:hidden bg-[#0A1F44] border-t border-[rgba(200,150,31,0.25)]" : "lg:hidden bg-white border-t border-[#ECEEF3]"}
          >
            <div className="px-6 py-6 flex flex-col">
              {NAV.map((n) => (
                <a
                  key={n.id}
                  href={`/#${n.id}`}
                  onClick={(e) => handleNav(e, n.id)}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className={[
                    "py-3 border-b font-display text-2xl",
                    dark ? "border-white/10" : "border-[#ECEEF3]",
                    activeId === n.id ? "text-[#C8961F]" : (dark ? "text-white" : "text-[#0A1F44]"),
                  ].join(" ")}
                >
                  {n.label}
                </a>
              ))}
              <a
                href="/#contact"
                onClick={(e) => handleNav(e, "contact")}
                data-testid="nav-mobile-cta"
                className={dark ? "btn btn--outline-gold mt-6 justify-center" : "btn btn--dark mt-6 justify-center"}
              >
                Enquire <ArrowRight className="arrow h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
